import { Dialog } from '@progress/kendo-react-dialogs';
import { SubmitButton, TextField } from 'app/components/form-fields';
import { BackActionDialogContentLayout } from 'app/components/layouts';
import useClickOutside from 'app/hooks/useClickOutside';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import './EditFleetDialog.less';
import { EquipmentSelect } from 'app/components/access-sharing/EquipmentSelect';
import { useRef, useState } from 'react';
import ill_warning from 'assets/images/ill_warning.svg';
import { ErrorModal } from 'app/components/ErrorModal';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import useFormField from 'app/hooks/form-fields/useFormField';
import useInputPatternValidation from 'app/hooks/form-fields/useInputPatternValidation';
import { lettersNumbersAndSpecialsRegex } from 'app/common/utils';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import type { InputHandle } from '@progress/kendo-react-inputs';

interface Props {
  initialName: string;
  exposedEquipmentIds?: string[];
  isLoading?: boolean;
  onChange: (value: { name: string; exposedEquipmentIds: string[] }) => void;
  onClose: () => void;
}

export default function EditFleetDialog({
  initialName,
  exposedEquipmentIds = [],
  isLoading,
  onChange,
  onClose,
}: Props) {
  const { t } = useTranslation();

  const inputRef = useRef<InputHandle>(null);

  // Note: there's a point in creating a wrapper around the Dialog and adding an onClickOutside prop
  useClickOutside(onClose);
  const { equipments } = useGetEquipmentData();

  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState(
    new Set(exposedEquipmentIds),
  );

  const [
    isSelectAtLeastOneItemDialogOpen,
    setIsSelectAtLeastOneItemDialogOpen,
  ] = useState(false);

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const {
    value: name,
    setValue: setName,
    error: nameError,
    setError: setNameError,
  } = useFormField(initialName);
  const { isPatternMatch, onKeyDown } = useInputPatternValidation(
    lettersNumbersAndSpecialsRegex,
  );

  function handleUpdate() {
    if (!validateName()) {
      inputRef?.current?.focus();
      inputRef?.current?.element?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }

    if (!selectedEquipmentIds.size) {
      setIsSelectAtLeastOneItemDialogOpen(true);
      return;
    }

    onChange({
      name: name?.trim() ?? '',
      exposedEquipmentIds: Array.from(selectedEquipmentIds),
    });
  }

  function validateName() {
    if (!name?.trim()) {
      setNameError(
        t('access_sharing.dialogs.edit_fleet_name.field_is_required'),
      );
      return false;
    }

    return true;
  }

  const handleChange = (e: InputChangeEvent) => {
    const inputValue = e.target.value as string;

    if (isPatternMatch(inputValue)) {
      setName(inputValue);
      setNameError('');
    }
  };

  return (
    <>
      <Dialog className="modal-container">
        <BackActionDialogContentLayout
          withShadow={false}
          className="edit-fleet-dialog"
          onBack={onClose}
        >
          <h2 className="edit-fleet-dialog__title">
            {t('access_sharing.dialogs.edit_fleet.title')}
          </h2>

          <p className="edit-fleet-dialog__subtitle">
            {t('access_sharing.dialogs.edit_fleet.subtitle')}
          </p>

          <TextField
            ref={inputRef}
            label={t('access_sharing.dialogs.edit_fleet_name.label')}
            required
            value={name}
            placeholder={t(
              'access_sharing.dialogs.edit_fleet_name.placeholder',
            )}
            maxLength={32}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            disabled={isLoading}
            error={nameError}
          />

          <EquipmentSelect
            equipment={equipments || []}
            selectedEquipmentIds={selectedEquipmentIds}
            className="edit-fleet-dialog__equipment-select"
            setSelectedEquipmentIds={setSelectedEquipmentIds}
            setIsSubmitDisabled={setIsSubmitDisabled}
            type="fleet"
          />

          <SubmitButton
            disabled={isLoading || isSubmitDisabled}
            onClick={handleUpdate}
          >
            <div className="edit-fleet-dialog__continue-btn-content">
              {t('save')}

              {selectedEquipmentIds.size > 0 && (
                <span className="edit-fleet-dialog__equipment-selected-text">
                  {t('no_equipment_selected', {
                    no: selectedEquipmentIds.size,
                  })}
                </span>
              )}
            </div>
          </SubmitButton>
        </BackActionDialogContentLayout>
      </Dialog>

      {/* TODO move into a separate component */}
      {isSelectAtLeastOneItemDialogOpen && (
        <Dialog className="modal-center modal-error modal-remove-item modal-fulltext">
          <ErrorModal
            icon={ill_warning}
            title={t('select_at_least_1_item')}
            class="select-items-modal"
            onClose={() => {
              setIsSelectAtLeastOneItemDialogOpen(false);
            }}
          />
        </Dialog>
      )}
    </>
  );
}
